type LiveHint = {
  kind: 'events' | 'calls';
  conversationId: string;
  latestCreatedAt?: string | null;
  latestCursor?: string | null;
};

type LiveTicket = {
  ticket: string;
  conversationId: string;
  expiresAt: string;
};

export type DialerLiveFanoutState = 'connecting' | 'live' | 'retrying' | 'closed';

type WebSocketLike = {
  readyState: number;
  onopen: (() => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
  close: () => void;
};

type TimerHandle = number;
type SetTimeoutLike = (callback: () => void, delayMs: number) => TimerHandle;
type ClearTimeoutLike = (handle: TimerHandle) => void;

function resolveApiBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return import.meta.env.VITE_API_URL || window.location.origin;
  }

  return import.meta.env.VITE_API_URL || 'http://localhost:8787';
}

function buildLiveFanoutUrl(ticket: string) {
  const url = new URL('/api/realtime/live', resolveApiBaseUrl());
  url.searchParams.set('ticket', ticket);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function isLiveHint(value: unknown): value is LiveHint {
  if (!value || typeof value !== 'object') return false;

  const typed = value as Partial<LiveHint>;
  return (
    (typed.kind === 'events' || typed.kind === 'calls') &&
    typeof typed.conversationId === 'string' &&
    typed.conversationId.length > 0
  );
}

export function startDialerLiveFanoutSession(input: {
  conversationId: string;
  requestTicket: (conversationId: string) => Promise<LiveTicket>;
  shouldStayConnected: () => boolean;
  onHint: (hint: LiveHint) => void;
  onStateChange?: (state: DialerLiveFanoutState) => void;
  createSocket?: (url: string) => WebSocketLike;
  hintCoalesceWindowMs?: number;
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
  setTimeoutFn?: SetTimeoutLike;
  clearTimeoutFn?: ClearTimeoutLike;
}) {
  const createSocket =
    input.createSocket ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
  const setTimeoutFn =
    input.setTimeoutFn ?? ((callback, delayMs) => window.setTimeout(callback, delayMs));
  const clearTimeoutFn = input.clearTimeoutFn ?? ((handle) => window.clearTimeout(handle));
  const hintCoalesceWindowMs = Math.max(0, input.hintCoalesceWindowMs ?? 0);
  const reconnectBaseMs = input.reconnectBaseMs ?? 500;
  const reconnectMaxMs = input.reconnectMaxMs ?? 5_000;

  let closed = false;
  let reconnectAttempt = 0;
  let reconnectTimer: TimerHandle | null = null;
  let hintFlushTimer: TimerHandle | null = null;
  let socket: WebSocketLike | null = null;
  let currentState: DialerLiveFanoutState = 'closed';
  const pendingHints = new Map<LiveHint['kind'], LiveHint>();

  const setState = (nextState: DialerLiveFanoutState) => {
    if (currentState === nextState) return;
    currentState = nextState;
    input.onStateChange?.(nextState);
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      clearTimeoutFn(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const clearHintFlushTimer = () => {
    if (hintFlushTimer !== null) {
      clearTimeoutFn(hintFlushTimer);
      hintFlushTimer = null;
    }
  };

  const flushPendingHints = () => {
    clearHintFlushTimer();
    const hints = [...pendingHints.values()];
    pendingHints.clear();
    for (const hint of hints) {
      input.onHint(hint);
    }
  };

  const queueHint = (hint: LiveHint) => {
    const existing = pendingHints.get(hint.kind);
    pendingHints.set(hint.kind, {
      kind: hint.kind,
      conversationId: hint.conversationId,
      latestCreatedAt: hint.latestCreatedAt ?? existing?.latestCreatedAt,
      latestCursor: hint.latestCursor ?? existing?.latestCursor,
    });

    if (hintCoalesceWindowMs === 0) {
      flushPendingHints();
      return;
    }

    if (hintFlushTimer !== null) return;
    hintFlushTimer = setTimeoutFn(() => {
      hintFlushTimer = null;
      flushPendingHints();
    }, hintCoalesceWindowMs);
  };

  const scheduleReconnect = () => {
    if (closed || !input.shouldStayConnected()) return;

    clearReconnectTimer();
    setState('retrying');
    const delayMs = Math.min(reconnectBaseMs * 2 ** reconnectAttempt, reconnectMaxMs);
    reconnectAttempt += 1;
    reconnectTimer = setTimeoutFn(() => {
      reconnectTimer = null;
      void connect();
    }, delayMs);
  };

  const handleClose = () => {
    socket = null;
    scheduleReconnect();
  };

  const connect = async () => {
    if (closed || !input.shouldStayConnected()) return;
    setState('connecting');

    try {
      const ticket = await input.requestTicket(input.conversationId);
      if (closed || !input.shouldStayConnected()) return;

      const nextSocket = createSocket(buildLiveFanoutUrl(ticket.ticket));
      socket = nextSocket;
      nextSocket.onopen = () => {
        reconnectAttempt = 0;
        setState('live');
      };
      nextSocket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as unknown;
          if (!isLiveHint(parsed) || parsed.conversationId !== input.conversationId) {
            return;
          }
          queueHint(parsed);
        } catch {
          // Ignore malformed websocket payloads and keep the session alive.
        }
      };
      nextSocket.onerror = () => undefined;
      nextSocket.onclose = handleClose;
    } catch {
      scheduleReconnect();
    }
  };

  void connect();

  return {
    close() {
      closed = true;
      clearReconnectTimer();
      clearHintFlushTimer();
      pendingHints.clear();
      setState('closed');
      const activeSocket = socket;
      socket = null;
      if (activeSocket && activeSocket.readyState < 2) {
        activeSocket.close();
      }
    },
  };
}
